import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
// Aliased: the outside-click effect below listens on `document` and wants the
// DOM `MouseEvent`, while the row handler gets React's synthetic one.
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';

/**
 * The Data Bar's checkbox multi-select row — a caption, a one-line summary of
 * what is ticked, and a popup list of checkboxes.
 *
 * WHEN the edits are written is the caller's business, not this widget's. It
 * reports a toggle (`onToggle`) and a close (`onCommit`), and the two rows
 * answer them differently: Walls, Roof & Floor batches and writes on the
 * close, while Bases writes on a 400 ms debounce after each toggle and
 * treats the close as "send it now" (`baseSidesWriter.ts`).
 *
 * Extracted from TunnelCabinetRow.tsx, where it was the "Walls, Roof & Floor"
 * row, when "Bases" became a second one of exactly the same widget. Every
 * behaviour here is that row's, moved verbatim: the markup shape, the drop
 * direction, the ellipsis tooltip and its re-measurement, commit-on-
 * outside-click, and the in-flight guard. Each is explained at its own site
 * below, and the reasons are the row's own history — none of it is new.
 *
 * CONTROLLED on the values. The caller owns the pending edits and the save,
 * because that is where the two rows differ: Walls/Roof & Floor PATCHes five
 * tunnel columns and has follow-up invalidations for Floor, while Bases
 * PATCHes four others, re-reads the lug placements and drops any toggle that
 * ends up where it started. Neither row restricts WHICH boxes may be
 * unticked -- all of them may be. What is shared is the interaction, and that
 * is all this holds state for.
 */

/* What `.tunnel-face-flag-edit-list.active` adds on top of the list's own
 * content height (ahuBoardOverrides.scss): 12px + 19px padding and the two 1px
 * borders. Mirrored here because the open height has to be known BEFORE the
 * popup opens, to pick the side it opens on. */
const FACE_FLAGS_OPEN_CHROME = 33;

/* The popup's 4px overlap of the row, from the stylesheet's `bottom: 4px`. */
const FACE_FLAGS_EDGE_GAP = 4;

/** Nearest scrolling ancestor — the Data Bar panel that clips this popup
 * (`#ahu_tab_container`), found by computed style rather than by id so a panel
 * that gains a scrollbar later is covered too. Same walk as PaginatedSelect.tsx,
 * which escapes the same clip for its option menus. */
const scrollParent = (el: HTMLElement): HTMLElement | null => {
  for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if (oy === 'auto' || oy === 'scroll') return n;
  }
  return null;
};

export interface FaceFlagOption {
  /** Checkbox `name` — the caller's own key, used verbatim in `values`. */
  name: string;
  label: string;
  /**
   * Draw a hairline under this row, separating it from the ones below.
   *
   * Opt-in per option, so only a caller that asks for it gets one: Bases
   * puts its "Default" row at the top and a rule under it, because Default is
   * not a fifth face but the switch that governs the four below it. Walls,
   * Roof & Floor passes nothing and is untouched. The border is the popup's
   * own `#ebebeb`, in `ahuBoardOverrides.scss` next to the list it belongs to.
   */
  dividerAfter?: boolean;
  /**
   * Keep this option out of the row's one-line summary (and its tooltip),
   * however it is ticked. It still appears in the dropdown and still works.
   *
   * For Bases' "Default", which is not a side of anything: the summary is a
   * list of the base sides this tunnel builds, and "Default" in among them
   * read as a fifth one (Shushanik, 2026-10-30). Walls, Roof & Floor passes
   * nothing and lists all six faces as before.
   */
  hideFromSummary?: boolean;
}

interface TunnelFaceFlagMultiSelectProps {
  options: ReadonlyArray<FaceFlagOption>;
  /** Tick state per option name, pending edits already folded in. */
  values: Record<string, boolean>;
  /** Option names the user may not toggle, with a reason for the tooltip. */
  lockedReason?: (name: string) => string | undefined;
  onToggle: (name: string) => void;
  /**
   * Commit the pending edits. Resolve (or reject) when the write has settled;
   * the popup closes either way, as it did when the PATCH lived in the row.
   * Called with nothing pending too — return early there, as the row did.
   */
  onCommit: () => Promise<unknown> | void;
  /** The row's caption, e.g. "Walls, Roof & Floor". */
  caption: ReactNode;
  /** DOM id for the icon button. One per row: ids must stay unique. */
  buttonId: string;
}

const TunnelFaceFlagMultiSelect = ({
  options,
  values,
  lockedReason,
  onToggle,
  onCommit,
  caption,
  buttonId,
}: TunnelFaceFlagMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [dropDown, setDropDown] = useState(false);
  const rowRef = useRef<HTMLLIElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // What the summary shows, and so also what the ellipsis tooltip repeats and
  // what the truncation is measured against: ticked, minus anything the
  // caller keeps out of the summary.
  const summarised = options.filter(({ name, hideFromSummary }) => values[name] && !hideFromSummary);
  const checkedText = summarised.map(({ label }) => label).join(', ');

  // Guards a commit that is still resolving. Walls, Roof & Floor returns its
  // PATCH from `onCommit`, so the list stays open and clickable for that
  // round-trip and a second click used to re-send the same payload —
  // reachable by double-clicking the icon, and easier to hit now that the
  // whole row is the activator. One write per commit. (Bases returns
  // nothing: its write is already on its way, and its own writer is what
  // keeps that to one request.)
  const savingRef = useRef(false);

  const commit = useCallback(() => {
    if (savingRef.current) return;
    savingRef.current = true;
    Promise.resolve(onCommit())
      .catch(() => undefined)
      .finally(() => {
        savingRef.current = false;
        setOpen(false);
      });
  }, [onCommit]);

  // The tooltip is the ellipsis's companion, so it appears only while there IS
  // an ellipsis — `scrollWidth > clientWidth` on the (nowrap, overflow:hidden)
  // summary. Four faces fit the 185px box and want no tooltip; six do not.
  // Measured off the DOM rather than from the string length because the
  // separators are CSS `::after` content and the font is whatever the data bar
  // resolved to.
  //
  // Measured on every summary change AND again on pointer-enter. The summary
  // text is not the only thing that can move the answer: the row mounts inside
  // a collapsed `.sublist`, the web font can resolve after the first commit,
  // and browser zoom re-lays the 185px box out from under a stored `true`/
  // `false`. Any of those leaves the flag stale, and a stale `false` is exactly
  // "hovering does nothing". Re-measuring on enter costs one layout read at the
  // only moment the answer is used, and lands before the shared tooltip's 0.5s
  // reveal delay, so the bubble still appears on the first hover.
  const summaryRef = useRef<HTMLSpanElement | null>(null);
  const [truncated, setTruncated] = useState(false);
  const measure = useCallback(() => {
    const el = summaryRef.current;
    const isTruncated = el ? el.scrollWidth > el.clientWidth : false;
    setTruncated((prev) => (prev === isTruncated ? prev : isTruncated));
  }, []);
  useEffect(measure, [checkedText, measure]);

  // Both halves of the row carry the tooltip — the caption as well as the
  // summary. The caption is what the row is *called*, so it is what people
  // point at when they want to see the faces the ellipsis ate; hanging the
  // tooltip on the value alone made hovering the row look broken. The caption
  // is a float, not an ancestor of the `position: absolute` popup, so giving
  // it `.ka-has-tooltip`'s `position: relative` cannot move that popup the way
  // promoting the `li`/`div` would.
  //
  // `tooltip` is a non-standard attribute, hence the spread — same trick as
  // FanCurveCanvas.tsx's zoom buttons.
  const tooltipClass = truncated ? ' ka-has-tooltip f-size-11' : '';
  const tooltipAttr = truncated ? { tooltip: checkedText } : {};

  // Which side the popup opens on, decided at the moment it opens.
  //
  // It is `position: absolute; bottom: 4px` against this `li`, so by default it
  // opens UPWARD — legacy's placement, inherited with the rest of the block from
  // `.certification-edit-list`. Upward is right for a row in the middle of the
  // tab and wrong for one near the top of it: the Data Bar panel
  // (`#ahu_tab_container`, `overflow: auto`) CLIPS whatever rises past its top
  // edge, and the AHU name sits immediately above that edge, so the first face
  // ends up half-hidden behind the tag. That is what a 1:1:1 section shows —
  // its Walls, Roof & Floor row is only two rows into the tab, because the Skin
  // Finish and Floor Under-* rows above it are hidden whenever their selects
  // come back empty.
  //
  // So: flip to a downward drop only when the popup does not fit above and has
  // more room below. Upward stays the default, so every row that is placed
  // correctly today is left exactly as it was.
  //
  // The open height is taken from the `ul` INSIDE the still-closed popup: the
  // popup itself is `max-height: 0` until `.active` and so measures 0, while
  // the list it clips is laid out at full height all along. Opening adds only
  // FACE_FLAGS_OPEN_CHROME on top of it.
  //
  // All four lengths are read as `getBoundingClientRect` so they share one unit.
  // They are not CSS px: the board scales itself with `document.body.style.zoom`
  // (see ahuBoardOverrides.scss) and the Data Bar nests more `zoom` rules inside
  // that, so `offsetHeight`-style lengths are ~25% larger than the rects here —
  // mixing the two flips rows that have ample room. The stylesheet's own 33px of
  // padding is the one CSS-px number in the sum, so it is scaled by the ratio the
  // list itself is drawn at. (PaginatedSelect.tsx hits the same wall and keeps a
  // `cssZoom` helper for it.)
  //
  // Measured once per open, not on scroll: the popup scrolls with its row (it
  // is absolute inside the panel, not fixed outside it), so the only thing that
  // can change the answer while it is open is scrolling the panel, and legacy
  // did not reposition on that either.
  const shouldDropDown = (): boolean => {
    const row = rowRef.current;
    const list = listRef.current;
    const inner = list?.firstElementChild as HTMLElement | null;
    if (!row || !inner) return false;
    const panel = scrollParent(row);
    if (!panel) return false;
    const rowRect = row.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const innerRect = inner.getBoundingClientRect();
    const zoom = inner.offsetHeight > 0 ? innerRect.height / inner.offsetHeight : 1;
    const needed = innerRect.height + FACE_FLAGS_OPEN_CHROME * zoom;
    const above = rowRect.bottom - FACE_FLAGS_EDGE_GAP * zoom - panelRect.top;
    const below = panelRect.bottom - rowRect.bottom;
    return needed > above && below > above;
  };

  // The WHOLE ROW is the activator, not just the icon button: clicking the
  // caption, the summary of checked faces, or the gap between them opens the
  // multiselect. The caption is the widest thing in the row and the natural
  // thing to aim at, so a 20px icon being the only way in read as a dead row.
  //
  // One handler for the row, and the button deliberately has no onClick of its
  // own — its click bubbles to this `li`, so any click is handled exactly once
  // and one click is still one write.
  //
  // Toggle, matching what the button did: open when closed, and when already
  // open COMMIT and close (legacy's `tunnel:face-flags:save`), so clicking the
  // caption a second time behaves as it did before this change.
  //
  // Clicks inside `.tunnel-face-flag-edit-list` are the exception — the
  // checkboxes are descendants of this `li` too, and toggling a face must not
  // close the popup out from under the next one.
  const toggleRow = (e: ReactMouseEvent<HTMLLIElement>) => {
    if (listRef.current?.contains(e.target as Node)) return;
    if (open) commit();
    else {
      setDropDown(shouldDropDown());
      setOpen(true);
    }
  };

  // Clicking outside the open multiselect closes it — and COMMITS, because
  // legacy's outside-click path is `main.js` closeOpenedBoxes (bound to the
  // board's `click`), which fires `tunnel:face-flags:save` → the same
  // saveTunnelFaceFlags the activator runs. It is a commit, not a discard; a
  // no-op when nothing was toggled.
  //
  // "Outside" is the row, not legacy's narrower
  // `#show_tunnel_face_flags`-or-inside-the-list set: everything within the
  // `li` — button, list, caption, summary — is handled by toggleRow above, and
  // must stay excluded here or both paths would fire the commit on one click
  // and send two writes. The user-visible result for a click on the caption or
  // summary is unchanged (commit + close); it is the row handler doing it
  // rather than this listener.
  useEffect(() => {
    if (!open) return undefined;
    const onDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (rowRef.current?.contains(target)) return;
      commit();
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [open, commit]);

  /* Markup is the legacy tab.ahu.cabinet.tmpl block verbatim: an
     `li.tunnel-face-flag-group` wrapping a bare div that holds the icon
     button, the edit list, the checked-flags summary and the label, in
     that order. Every rule this widget has in ahuBoardOverrides.scss is
     scoped under `.tunnel-face-flag-group`, so the shape has to match or
     none of them apply. The edit list stays mounted and opens via
     `.active` (legacy showTunnelFaceFlags/hideTunnelFaceFlags) — it is a
     max-height transition on a position:absolute popup, not a mount.

     The click handler is on the `li`, the row itself, so the whole row
     opens the multiselect — including any padding outside the inner div.
     `onClick` on an `li` changes no CSS (it is not `position` or a class),
     so the absolutely-positioned popup still resolves against the same
     containing block it always did. */
  return (
    <li ref={rowRef} className="tunnel-face-flag-group" onClick={toggleRow}>
      {/* One listener for the whole row: React fires `onMouseEnter` on the
          div whenever the pointer crosses into its subtree from outside,
          so it covers the caption, the summary and the icon button alike —
          whichever half the user actually aims at, the measurement is
          refreshed before the tooltip is asked for. */}
      <div onMouseEnter={measure}>
        <button
          id={buttonId}
          type="button"
          className="icon-mounting icon-selection"
          tabIndex={-1}
        />
        <div
          ref={listRef}
          className={`tunnel-face-flag-edit-list${open ? ' active' : ''}${
            dropDown ? ' drop-down' : ''
          }`}
        >
          <ul>
            {options.map(({ name, label, dividerAfter }) => {
              const locked = lockedReason?.(name);
              return (
                <li
                  key={name}
                  className={dividerAfter ? 'tunnel-face-flag-divider' : undefined}
                >
                  {/* A disabled box with the app's own tooltip, not a click
                      silently ignored: the only thing that locks one is a
                      frozen or static configuration, and the user needs to
                      be told that rather than left clicking a dead box. */}
                  <label
                    className={locked ? 'ka-has-tooltip f-size-11' : undefined}
                    {...(locked ? { tooltip: locked } : {})}
                  >
                    <input
                      type="checkbox"
                      className="tunnel-face-flag"
                      name={name}
                      checked={Boolean(values[name])}
                      disabled={Boolean(locked)}
                      onChange={() => onToggle(name)}
                      tabIndex={-1}
                    />
                    <span className="label">{label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
        {/* One <span> per checked face, no JS separator: legacy draws the
            commas with `.checked-tunnel-face-flags span:not(:empty):not(
            :last-child):after { content: ', ' }`. Joining in JS would
            collapse them into one text node and lose the per-span
            font/colour/nowrap rules with it.

            The summary is kept to ONE line (the container is `nowrap` +
            `text-overflow: ellipsis`, see ahuBoardOverrides.scss), so six
            checked faces read as "Upstream, Downstream, Left, Right,…"
            rather than wrapping and pushing the rest of the data bar down.
            Order is `options` order throughout, minus any option the caller
            marked `hideFromSummary` (Bases' "Default").

            The `{' '}` after each span stays: every span is
            `white-space: nowrap` and the comma lives in the span's own
            `::after`, so without it the faces would run together as
            "Upstream,Downstream".

            The hover tooltip is the app-wide `.ka-has-tooltip` + `tooltip`
            attribute widget (common.css:31569-31634, `content:
            attr(tooltip)` on `:hover:after` — grey #8d8d8d bubble, 10px
            Roboto Medium, 0.5s delay), not a native `title`, so it matches
            every other tooltip on the board. Two things force it onto a
            WRAPPER rather than the summary span itself:

              · `.ka-has-tooltip` is `position: relative` and the bubble is
                its own `::after`. On the summary — `overflow: hidden`, the
                very rule that draws the ellipsis — the bubble would be
                clipped to the 185px box and never seen.
              · the `li` and its `div` are out too: neither is positioned
                today, and the face-flags popup above is `position:
                absolute` against the data bar with a tuned `margin-left:
                230px`/`bottom: 4px`. Making either an ancestor containing
                block would move the popup.

            `f-size-11` is the shared 11px modifier from the same block
            (common.css:31635), not a size of our own: it is what the other
            tooltips in this Data Bar already use (InternalWallsGroup.tsx,
            DmountGroup.tsx), so the row reads at the same size as its
            neighbours rather than at the 10px base. */}
        <span className={`checked-tunnel-face-flags-anchor${tooltipClass}`} {...tooltipAttr}>
          <span ref={summaryRef} className="checked-tunnel-face-flags output">
            {summarised.map(({ name, label }) => (
              <Fragment key={name}>
                <span>{label}</span>{' '}
              </Fragment>
            ))}
          </span>
        </span>
        <span className={`label${tooltipClass}`} {...tooltipAttr}>
          {caption}
        </span>
      </div>
    </li>
  );
};

export default TunnelFaceFlagMultiSelect;
